// frontend/src/pages/admin/DashboardPage.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  FolderTree, 
  Newspaper, 
  Users, 
  Settings, 
  ShoppingBag,
  TrendingUp,
  Eye,
  Edit,
  Trash2,
  Plus,
  Loader2 
} from 'lucide-react';
import { PageSEO } from '../../components/SEO/PageSEO';
import api from '../../services/api';

export const DashboardPage = () => {
  const [stats, setStats] = useState({
    products: 0,
    categories: 0,
    blogs: 0,
    users: 0,
    orders: 0,
  });
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // دریافت آمار محصولات
      const productsRes = await api.get('/products/products/', { params: { page_size: 1 } });
      // دریافت آمار دسته‌بندی‌ها
      const categoriesRes = await api.get('/products/categories/');
      // دریافت آمار بلاگ
      const blogsRes = await api.get('/cms/blog/', { params: { page_size: 1 } });
      // دریافت آخرین محصولات
      const recentProductsRes = await api.get('/products/products/', { params: { page_size: 5, ordering: '-created_at' } });
      // دریافت آخرین مقالات
      const recentBlogsRes = await api.get('/cms/blog/', { params: { page_size: 5, ordering: '-published_at' } });

      setStats({
        products: productsRes.data.count || 0,
        categories: categoriesRes.data.results?.length || categoriesRes.data?.length || 0,
        blogs: blogsRes.data.count || 0,
        users: 0, // اگر API کاربران دارید
        orders: 0, // اگر API سفارشات دارید
      });
      setRecentProducts(recentProductsRes.data.results || recentProductsRes.data || []);
      setRecentBlogs(recentBlogsRes.data.results || recentBlogsRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { title: 'محصولات', icon: Package, path: '/admin/products', color: 'bg-blue-500', description: 'مدیریت محصولات فروشندگان' },
    { title: 'دسته‌بندی‌ها', icon: FolderTree, path: '/admin/categories', color: 'bg-green-500', description: 'مدیریت دسته‌بندی‌ها' },
    { title: 'مقالات', icon: Newspaper, path: '/admin/blog', color: 'bg-purple-500', description: 'مدیریت مقالات و بلاگ' },
    { title: 'کاربران', icon: Users, path: '/admin/users', color: 'bg-yellow-500', description: 'مدیریت کاربران و نقش‌ها' },
    { title: 'سفارشات', icon: ShoppingBag, path: '/admin/orders', color: 'bg-orange-500', description: 'مدیریت سفارشات' },
    { title: 'تنظیمات سایت', icon: Settings, path: '/admin/settings', color: 'bg-gray-500', description: 'تنظیمات عمومی و سئو' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="داشبورد مدیریت | پنل ادمین" noindex={true} />
      
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">داشبورد مدیریت</h1>
            <p className="text-gray-600 mt-1">به پنل مدیریت سان ایران خوش آمدید</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">محصولات</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.products}</p>
                </div>
                <Package className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">دسته‌بندی‌ها</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.categories}</p>
                </div>
                <FolderTree className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">مقالات</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.blogs}</p>
                </div>
                <Newspaper className="w-10 h-10 text-purple-500 opacity-50" />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">کاربران</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.users}</p>
                </div>
                <Users className="w-10 h-10 text-yellow-500 opacity-50" />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">سفارشات</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.orders}</p>
                </div>
                <ShoppingBag className="w-10 h-10 text-orange-500 opacity-50" />
              </div>
            </div>
          </div>

          {/* Menu Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
              >
                <div className="p-6">
                  <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-500 text-sm">{item.description}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Recent Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Products */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-500" />
                  <h2 className="text-lg font-bold text-gray-900">آخرین محصولات</h2>
                </div>
                <Link to="/admin/products" className="text-sm text-blue-600 hover:text-blue-700">
                  مشاهده همه
                </Link>
              </div>
              <div className="divide-y divide-gray-100">
                {recentProducts.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">محصولی یافت نشد</div>
                ) : (
                  recentProducts.map((product) => (
                    <div key={product.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.main_image || '/placeholder-product.jpg'}
                          alt={product.name}
                          className="w-10 h-10 rounded object-cover"
                        />
                        <div>
                          <p className="font-medium text-gray-900 line-clamp-1">{product.name}</p>
                          <p className="text-sm text-gray-500">{product.price?.toLocaleString()} تومان</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/products/${product.slug}`} className="text-green-600 hover:text-green-800">
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link to={`/admin/products/${product.slug}/edit`} className="text-blue-600 hover:text-blue-800">
                          <Edit className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Recent Blogs */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-purple-500" />
                  <h2 className="text-lg font-bold text-gray-900">آخرین مقالات</h2>
                </div>
                <Link to="/admin/blog" className="text-sm text-blue-600 hover:text-blue-700">
                  مشاهده همه
                </Link>
              </div>
              <div className="divide-y divide-gray-100">
                {recentBlogs.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">مقاله‌ای یافت نشد</div>
                ) : (
                  recentBlogs.map((blog) => (
                    <div key={blog.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img
                          src={blog.featured_image || '/placeholder-image.jpg'}
                          alt={blog.title}
                          className="w-10 h-10 rounded object-cover"
                        />
                        <div>
                          <p className="font-medium text-gray-900 line-clamp-1">{blog.title}</p>
                          <p className="text-sm text-gray-500">{blog.view_count || 0} بازدید</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/blog/${blog.slug}`} className="text-green-600 hover:text-green-800">
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link to={`/admin/blog/${blog.slug}/edit`} className="text-blue-600 hover:text-blue-800">
                          <Edit className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};